"use client";

import React from "react";
import { Tabs as AntTabs, ConfigProvider } from "antd";
import type { TabsProps as AntTabsProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import "../styles/tabs.css";

export type TabVariant = "pill" | "segmented" | "control";

export interface TabsProps extends AntTabsProps {
  /**
   * The variant/type of tabs
   */
  variant?: TabVariant;

  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * Tabs component - Ant Design Tabs with OpenStore Orion theming
 *
 * This component provides FULL access to all Ant Design Tabs props while
 * automatically applying the OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Three Figma design variants (pill, segmented, control)
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Tabs features and props
 *
 * @example
 * ```tsx
 * // Type 1: Pill tabs (default)
 * <Tabs variant="pill" items={[
 *   { key: 'all', label: 'All' },
 *   { key: 'active', label: 'Active' },
 *   { key: 'draft', label: 'Draft' },
 *   { key: 'archived', label: 'Archived' }
 * ]} />
 *
 * // Type 2: Segmented buttons
 * <Tabs variant="segmented" items={[
 *   { key: 'once', label: 'Once' },
 *   { key: 'weekly', label: 'Weekly' },
 *   { key: 'monthly', label: 'Monthly' },
 *   { key: 'yearly', label: 'Yearly' }
 * ]} />
 *
 * // Type 3: Segmented control
 * <Tabs variant="control" items={[
 *   { key: 'left', label: 'Left' },
 *   { key: 'center', label: 'Center' },
 *   { key: 'right', label: 'Right' }
 * ]} />
 *
 * // All Ant Design Tabs props work
 * <Tabs
 *   variant="pill"
 *   activeKey={activeKey}
 *   onChange={handleChange}
 *   items={tabItems}
 *   size="large"
 *   tabPosition="top"
 *   type="card"
 *   centered
 *   className="custom-tabs"
 * />
 * ```
 */
export const Tabs: React.FC<TabsProps> = ({
  variant = "pill",
  className,
  ...props
}) => {
  const getVariantClass = (variant: TabVariant) => {
    switch (variant) {
      case "pill":
        return "orion-tabs-pill";
      case "segmented":
        return "orion-tabs-segmented";
      case "control":
        return "orion-tabs-control";
      default:
        return "orion-tabs-pill";
    }
  };

  return (
    <ConfigProvider theme={orionTheme}>
      <AntTabs
        className={clsx("orion-tabs", getVariantClass(variant), className)}
        {...props}
      />
    </ConfigProvider>
  );
};

Tabs.displayName = "Tabs";

// Export convenience components for each variant
export const PillTabs: React.FC<Omit<TabsProps, "variant">> = (props) => (
  <Tabs variant="pill" {...props} />
);

export const SegmentedTabs: React.FC<Omit<TabsProps, "variant">> = (props) => (
  <Tabs variant="segmented" {...props} />
);

export const ControlTabs: React.FC<Omit<TabsProps, "variant">> = (props) => (
  <Tabs variant="control" {...props} />
);

// Re-export Ant Design Tabs sub-components
export const TabPane = AntTabs.TabPane;
